import React, { useState, useMemo, ReactNode, RefObject } from "react"
import {
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Grid,
  Heading,
  Card,
  GridItem,
  Tag,
  Link,
  Portal,
} from "@chakra-ui/react"
import { Drawer } from "@chakra-ui/react"
import { Checkbox } from "@chakra-ui/react"
import { Alert } from "@hydrologiq/shared/chakra/Alert"
import { Order, OrderStatus } from "@hydrologiq/gql/graphql"
import StatusBadge from "../StatusBadge"
import { CloseButton, FilterButton } from "@hydrologiq/shared/content/components/Button"
import { formatYYYYMMDD, toUserTZ } from "@hydrologiq/common/helpers/date"

interface OrdersDrawerContentProps {
  orders: Order[]
  loading: boolean
  error: string | null
  onFilterOrder: (order?: Order) => void
  onLoadMore?: () => void
  hasMore?: boolean
  open: boolean
  onOpenChange: (details: { open: boolean }) => void
  container?: RefObject<HTMLElement | null>
}

export const OrdersDrawerContent: React.FC<OrdersDrawerContentProps> = ({
  orders,
  loading,
  error,
  onFilterOrder,
  onLoadMore,
  hasMore = false,
  open,
  onOpenChange,
  container,
}) => {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [showAllOrders, setShowAllOrders] = useState(false)

  // Sort orders: scheduled first, then started, completed, billed
  const sortedOrders = useMemo(() => {
    const statusOrder = {
      [OrderStatus.Scheduled]: 1,
      [OrderStatus.Started]: 2,
      [OrderStatus.Completed]: 3,
      [OrderStatus.Billed]: 4,
      [OrderStatus.Pending]: 5,
    }

    const filtered = showAllOrders ? orders : orders.filter((order) => order.status === OrderStatus.Scheduled)

    return [...filtered].sort((a, b) => {
      const orderA = statusOrder[a.status] || 999
      const orderB = statusOrder[b.status] || 999
      return orderA - orderB
    })
  }, [orders, showAllOrders])

  const mapStatusToType = (status: OrderStatus): "scheduled" | "in_progress" | "completed" | "billed" => {
    switch (status) {
      case OrderStatus.Scheduled:
        return "scheduled"
      case OrderStatus.Started:
        return "in_progress"
      case OrderStatus.Completed:
        return "completed"
      case OrderStatus.Billed:
        return "billed"
      default:
        return "scheduled"
    }
  }

  const Portalled = ({ children }: { children: ReactNode }) => {
    if (container && container.current) {
      return <Portal container={container}>{children}</Portal>
    }
    return children
  }

  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      placement="bottom"
      size="md"
      closeOnInteractOutside={false}
      modal={false}
    >
      <Portalled>
        <Drawer.Positioner pointerEvents="none">
          <Drawer.Content pointerEvents={"auto"} height="250px" scrollBehavior="inside" overflowY="scroll">
            <Drawer.Header>
              <Drawer.Title>Orders {/* Filter toggle */}</Drawer.Title>
              <HStack mr={5}>
                {selectedOrder && (
                  <Link
                    variant="primary"
                    onClick={(e) => {
                      e.preventDefault()
                      setSelectedOrder(null)
                      onFilterOrder(undefined)
                    }}
                  >
                    Clear selection ({selectedOrder.name})
                  </Link>
                )}
                <Checkbox.Root checked={showAllOrders} onCheckedChange={(e) => setShowAllOrders(e.checked === true)}>
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>Show all orders</Checkbox.Label>
                </Checkbox.Root>
              </HStack>
              <Drawer.CloseTrigger>
                <CloseButton />
              </Drawer.CloseTrigger>
            </Drawer.Header>

            <Drawer.Body>
              <VStack align="stretch" gap={4}>
                {/* Loading state */}
                {loading && sortedOrders.length === 0 && (
                  <HStack justify="center" p={8}>
                    <Spinner size="lg" />
                    <Text>Loading orders...</Text>
                  </HStack>
                )}

                {/* Error state */}
                {error && (
                  <Alert status="error" title="Error loading orders">
                    {error}
                  </Alert>
                )}

                {/* Empty state */}
                {!loading && !error && sortedOrders.length === 0 && (
                  <VStack p={8} gap={2}>
                    <Text color="gray.500">No orders found</Text>
                    {!showAllOrders && (
                      <Text fontSize="sm" color="fg.subtle">
                        Try enabling "Show all orders"
                      </Text>
                    )}
                  </VStack>
                )}

                <Grid templateColumns="repeat(auto-fill, 250px)" gap={2}>
                  {sortedOrders.map((order) => (
                    <GridItem>
                      <Card.Root size="sm" key={order.id} width="fit-content">
                        <Card.Header>
                          <HStack>
                            <Heading size="md">{order.name}</Heading>
                            <FilterButton
                              onClick={() => {
                                setSelectedOrder(order)
                                onFilterOrder(order)
                              }}
                              disabled={selectedOrder?.id === order.id}
                              context={{
                                label: "Select",
                                description: "Select this order to filter the calendar",
                                variant: "primary",
                              }}
                            />
                          </HStack>
                        </Card.Header>
                        <Card.Body color="fg.muted">
                          <VStack align="stretch" gap={2}>
                            <Grid templateColumns="repeat(2, 100px)" gap={1}>
                              <StatusBadge status={mapStatusToType(order.status)} />
                              <Tag.Root>
                                <Tag.Label>
                                  {(order.status === OrderStatus.Completed
                                    ? order.quantity_consumed
                                    : order.quantity
                                  )?.toFixed(0) || 0}{" "}
                                  kg
                                </Tag.Label>
                              </Tag.Root>
                              <Tag.Root>
                                <Tag.Label>{order.start ? formatYYYYMMDD(toUserTZ(order.start)) : "—"}</Tag.Label>
                              </Tag.Root>
                              <Tag.Root>
                                <Tag.Label>{order.end ? formatYYYYMMDD(toUserTZ(order.end)) : "Open-ended"}</Tag.Label>
                              </Tag.Root>
                            </Grid>
                            {/* Fulfilled quantity progress */}
                            <Tag.Root colorPalette="blue" width="100%">
                              <Tag.Label>
                                Planned: {order.quantity_fulfilled?.toFixed(0) || 0} / {order.quantity?.toFixed(0) || 0}{" "}
                                kg ({(((order.quantity_fulfilled || 0) / (order.quantity || 1)) * 100).toFixed(0)}%)
                              </Tag.Label>
                            </Tag.Root>
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    </GridItem>
                  ))}
                </Grid>

                {/* Load more button */}
                {hasMore && (
                  <Button onClick={onLoadMore} size="sm" variant="outline" disabled={loading}>
                    {loading ? "Loading..." : "Load More"}
                  </Button>
                )}
              </VStack>
            </Drawer.Body>

            <Drawer.CloseTrigger />
          </Drawer.Content>
        </Drawer.Positioner>
      </Portalled>
    </Drawer.Root>
  )
}

export default OrdersDrawerContent
